import { HeartIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { useEffect, useState } from 'react';
import { Box, Button, Flex, Link, Skeleton, Table, Text } from '@/components/common';
import { userService, type PhotoLike } from '@/services';
import type { ProfilePhoto, UserProfile } from '@/types';
import { formatRelativeTimestamp } from '@/utils/date';
import { PhotoThumbnail } from './PhotoThumbnail';

const TEASER_ROW_COUNT = 3;

function TeaserRow() {
  return (
    <Flex align="center" gap="3" py="2">
      <Skeleton width="40px" height="40px" style={{ borderRadius: 'var(--radius-full-pill)' }} />
      <Flex direction="column" gap="2" flexGrow="1">
        <Skeleton width="45%" height="10px" />
        <Skeleton width="30%" height="8px" />
      </Flex>
      <Skeleton width="40px" height="40px" style={{ borderRadius: 'var(--radius-btn)' }} />
    </Flex>
  );
}

function PaywalledLikesTeaser({
  hiddenCount,
  onUnlock,
}: {
  hiddenCount: number;
  onUnlock: () => void;
}) {
  const { t } = useTranslation('chat');
  return (
    <Box position="relative" data-testid="likes-paywall-teaser">
      <Flex
        direction="column"
        aria-hidden
        style={{ filter: 'blur(8px)', pointerEvents: 'none', userSelect: 'none' }}
      >
        {Array.from({ length: TEASER_ROW_COUNT }, (_, i) => (
          <TeaserRow key={i} />
        ))}
      </Flex>
      <Flex
        position="absolute"
        inset="0"
        direction="column"
        align="center"
        justify="center"
        gap="3"
      >
        <Text size="2" weight="bold" align="center">
          {t('likes.moreCount', { count: hiddenCount })}
        </Text>
        <Button type="button" size="sm" radius="full" onClick={onUnlock} data-testid="likes-unlock">
          {t('likes.unlockCta')}
        </Button>
      </Flex>
    </Box>
  );
}

function LikeRow({
  like,
  liker,
  photo,
}: {
  like: PhotoLike;
  liker?: UserProfile;
  photo?: ProfilePhoto;
}) {
  const { t } = useTranslation('chat');
  const { t: tCommon } = useTranslation('common');
  return (
    <Table.Row data-testid={`received-like-${like.id}`}>
      <Table.Cell>
        <PhotoThumbnail
          photo={photo ?? { id: like.photoId, url: '' }}
          size="3"
          fallback={<HeartIcon size={14} />}
          alt={t('likes.likedPhotoAlt')}
        />
      </Table.Cell>
      <Table.Cell>
        <Text size="2" as="p">
          <Link asChild weight="bold">
            <RouterLink to={`/profile/${like.fromUid}`}>{liker?.name ?? '…'}</RouterLink>
          </Link>{' '}
          {t('likes.rowCaption')}
        </Text>
      </Table.Cell>
      <Table.Cell justify="end">
        <Text size="1" color="gray" className="whitespace-nowrap">
          {formatRelativeTimestamp(like.createdAt, {
            yesterdayLabel: tCommon('time.yesterday'),
          })}
        </Text>
      </Table.Cell>
    </Table.Row>
  );
}

/** Received-like rows (already capped for free users) plus the paywall teaser. */
export function ReceivedLikesList({
  likes,
  photosById,
  hiddenCount,
  onUnlock,
}: {
  likes: PhotoLike[];
  photosById: Map<string, ProfilePhoto>;
  hiddenCount: number;
  onUnlock: () => void;
}) {
  const [likerCache, setLikerCache] = useState<Record<string, UserProfile>>({});

  useEffect(() => {
    if (likes.length === 0) return;

    let cancelled = false;

    void (async () => {
      const ids = [...new Set(likes.map(like => like.fromUid))];
      const profiles = await Promise.all(
        ids.map(async id => [id, await userService.getPublicProfile(id)] as const)
      );
      if (cancelled) return;

      setLikerCache(prev => {
        let changed = false;
        const next = { ...prev };
        for (const [id, profile] of profiles) {
          if (profile && !next[id]) {
            next[id] = profile;
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [likes]);

  return (
    <>
      <Table.Root>
        <Table.Body>
          {likes.map(like => (
            <LikeRow
              key={like.id}
              like={like}
              liker={likerCache[like.fromUid]}
              photo={photosById.get(like.photoId)}
            />
          ))}
        </Table.Body>
      </Table.Root>
      {hiddenCount > 0 && <PaywalledLikesTeaser hiddenCount={hiddenCount} onUnlock={onUnlock} />}
    </>
  );
}
